import React, { useEffect, useState } from "react";
import { useDeviceOperationData } from "../../Hooks/device/useDeviceOperationData";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import DeviceOperationPanel from "./DeviceOperationPanel";
import DeviceInfoPanel from "./DeviceInfoPanel";
import DeviceInfoMetrics from "./DeviceInfoMetrics";
import CreateDeviceModal from "./CreateDeviceModal";
import ManualQuotaDrawer from "../Firm/Tabs/ManualQuotaDrawer";
import ApkUpdateDrawer from "./ApkUpdateDrawer";
import HardwareUpdateDrawer from "./HardwareUpdateDrawer";

function getModalTitle(operation) {
  switch (operation) {
    case "Aktiflik":
      return "Kota Alma Durumu Değişimi";
    case "Firma":
      return "Firmaya Ver";
    case "Servis":
      return "Servise Al";
    case "Plasiyer":
      return "Plasiyere Ver";
    case "ManuelKota":
      return "Manuel Kota Yükleme";
    default:
      return "Cihaz İşlemi";
  }
}

function DeviceDetailPageHeader({
  deviceDetailIsLoading,
  totalDevices,
  devices,
  setDevices,
  deviceRevised,
  onRefresh,
  onDeviceClick = null,
  operationHandler = null,
  setOperationHandler,
  selectedOperationDevice,
  setSelectedOperationDevice,
  setSelectedDevice,
  onDeviceDetailClick,
  state,
  isDealer = false,
}) {
  const { auth } = useAuth();

  // Panel durumları
  const [showPanel, setShowPanel] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState("");

  // Info panel için yeni state
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Create device modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Manuel Kota: firma cihazlar sayfasındaki drawer tasarımı
  const [manualQuotaDrawer, setManualQuotaDrawer] = useState({ open: false, device: null });

  // APK Update drawer state
  const [showApkDrawer, setShowApkDrawer] = useState(false);

  // Hardware (kart firmware) Update drawer state
  const [showHardwareDrawer, setShowHardwareDrawer] = useState(false);

  useEffect(() => {
    // onDeviceDetailClick prop'undan gelen değeri kontrol et
    // Bu prop DetailDevicePage'den geliyor ve "all", "create", "apk", "hardware" olabilir
    if (onDeviceClick === "all") {
      setShowInfoPanel(true);
      setShowCreateModal(false);
      setShowApkDrawer(false);
      setShowHardwareDrawer(false);
    } else if (onDeviceClick === "create") {
      setShowCreateModal(true);
      setShowInfoPanel(false);
      setShowApkDrawer(false);
      setShowHardwareDrawer(false);
    } else if (onDeviceClick === "apk") {
      setShowApkDrawer(true);
      setShowInfoPanel(false);
      setShowCreateModal(false);
      setShowHardwareDrawer(false);
    } else if (onDeviceClick === "hardware") {
      setShowHardwareDrawer(true);
      setShowInfoPanel(false);
      setShowCreateModal(false);
      setShowApkDrawer(false);
    } else if (onDeviceClick && typeof onDeviceClick === "object") {
      // Device object is passed, show info panel
      setShowInfoPanel(true);
      setShowCreateModal(false);
      setShowApkDrawer(false);
      setShowHardwareDrawer(false);
    } else if (onDeviceClick === null || onDeviceClick === false) {
      // Reset all panels when null/false
      setShowInfoPanel(false);
      setShowCreateModal(false);
      setShowApkDrawer(false);
      setShowHardwareDrawer(false);
    }
  }, [onDeviceClick]);

  useEffect(() => {
    if (operationHandler === "ManuelKota") {
      setManualQuotaDrawer({ open: true, device: selectedOperationDevice });
      setOperationHandler(null);
    } else if (operationHandler) {
      handleOperationClick(operationHandler);
    }
  }, [operationHandler]);

  const {
    deviceOperationIsLoading,
    deviceOperationIsError,
    deviceOperationIsSuccess,
    deviceOperationErrorMessage,
    deviceOperationSuccessMessage,
    deviceOperationData,
    toggleDeviceActivity,
    moveDeviceToFirm,
    moveDeviceToPlaymaker,
    moveDeviceToService,
  } = useDeviceOperationData();

  // Yenileme için loading state'i
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Yenileme fonksiyonu
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  // Operasyon butonlarına tıklandığında
  const handleOperationClick = (operation) => {
    setSelectedOperation(operation);
    setShowPanel(true);
  };

  useEffect(() => {
    if (deviceOperationIsSuccess && deviceOperationData?.device) {
      deviceRevised(deviceOperationData?.device);
    }
  }, [deviceOperationIsSuccess]);

  // İşlemi onayla
  const handleConfirmOperation = (operationData) => {
    if (selectedOperation === "Aktiflik") {
      toggleDeviceActivity({
        token: auth.accessToken,
        id: operationData.device._id,
        connect: operationData.connect,
        description: operationData.description,
        isActive: !operationData.device.isActive,
      });
    } else if (selectedOperation === "Firma") {
      moveDeviceToFirm({
        token: auth.accessToken,
        id: operationData.device._id,
        connect: operationData.connect,
        firmName: operationData.firm ? operationData.firm?.firmName : "",
        description: operationData.description,
        isActive: operationData.isActive || false,
      });
    } else if (selectedOperation === "Plasiyer") {
      moveDeviceToPlaymaker({
        token: auth.accessToken,
        id: operationData.device._id,
        connect: operationData.connect,
        userID: operationData.playmaker ? operationData.playmaker?._id : "",
        description: operationData.description,
      });
    } else if (selectedOperation === "Servis") {
      moveDeviceToService({
        token: auth.accessToken,
        id: operationData.device._id,
        connect: operationData.connect,
        description: operationData.description,
        isActive: operationData.isActive ?? true,
      });
    }
  };

  const { hasPermission, isAdmin } = usePermission();

  return (
    <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
      <DeviceInfoMetrics
        deviceDetailIsLoading={deviceDetailIsLoading}
        totalDevices={totalDevices}
        devices={devices}
        onDeviceClick={(action) => {
          if (action === "all") {
            onDeviceDetailClick("all");
          } else if (action === "create") {
            onDeviceDetailClick("create");
          } else if (action === "apk") {
            onDeviceDetailClick("apk");
          } else if (action === "hardware") {
            onDeviceDetailClick("hardware");
          } else if (action === "card") {
            onDeviceDetailClick("card");
          }
        }}
        isAdmin={isAdmin}
        isDealer={isDealer}
      />
      {/* Operasyon Paneli - İşlem durumlarını prop olarak ver */}
      <DeviceOperationPanel
        open={showPanel}
        title={getModalTitle(selectedOperation)}
        devices={devices}
        onClose={() => {
          setShowPanel(false);
          setOperationHandler(null);
        }}
        onConfirm={handleConfirmOperation}
        operation={selectedOperation}
        deviceOperationIsLoading={deviceOperationIsLoading}
        deviceOperationIsSuccess={deviceOperationIsSuccess}
        deviceOperationIsError={deviceOperationIsError}
        deviceOperationErrorMessage={deviceOperationErrorMessage}
        deviceOperationSuccessMessage={deviceOperationSuccessMessage}
        selectedOperationDevice={selectedOperationDevice}
        setSelectedOperationDevice={setSelectedOperationDevice}
      />

      {/* Manuel Kota: firma cihazlar sayfasındaki drawer tasarımı */}
      <ManualQuotaDrawer
        open={manualQuotaDrawer.open}
        device={manualQuotaDrawer.device}
        onClose={() => {
          setManualQuotaDrawer({ open: false, device: null });
          setSelectedOperationDevice?.(null);
        }}
        onSuccess={() => {
          // Drawer "Tamamlandı"ya kadar açık kalır; sadece arka planı tazele
          onRefresh?.();
        }}
      />

      {/* Yeni Bilgi Paneli */}
      <DeviceInfoPanel
        open={showInfoPanel}
        onClose={() => {
          setSelectedDevice(null);
          setShowInfoPanel(false);
        }}
        devices={devices}
        setDevices={setDevices}
        onSelectedDevice={onDeviceClick}
        state={state}
      />

      {/* APK Drawer — görüntüleme devices.apkRead yetkisine bağlı; yükleme
          kısmı drawer içinde devices.apkUpdate ile ayrıca gate'lenir. */}
      {hasPermission("devices.apkRead") && (
        <ApkUpdateDrawer
          open={showApkDrawer}
          onClose={() => {
            setShowApkDrawer(false);
            if (onDeviceDetailClick) {
              onDeviceDetailClick(null);
            }
          }}
        />
      )}

      {/* Hardware Drawer — görüntüleme devices.hardwareRead yetkisine bağlı;
          yükleme/silme drawer içinde devices.hardwareUpdate ile ayrıca gate'lenir. */}
      {hasPermission("devices.hardwareRead") && (
        <HardwareUpdateDrawer
          open={showHardwareDrawer}
          onClose={() => {
            setShowHardwareDrawer(false);
            if (onDeviceDetailClick) {
              onDeviceDetailClick(null);
            }
          }}
        />
      )}

      {/* Cihaz Oluşturma Modal */}
      {hasPermission("devices.create") && <CreateDeviceModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          if (onDeviceDetailClick) {
            onDeviceDetailClick(null);
          }
        }}
        onSuccess={() => {
          // Cihaz oluşturulduktan sonra listeyi yenile
          if (onRefresh) {
            onRefresh();
          }
        }}
      />}
    </div>
  );
}

export default DeviceDetailPageHeader;
